import type { SiteFonts } from "@keepoinc/site-page-react-component";

import type { FormCustomField } from "./formFields";
import { preDefinedThemes, Theme } from "./theme";

export interface Site {
  title: string;
  /** Field of work: see model/categories. */
  category: string;
  /** The user's own words when `category` is "other". */
  categoryOther?: string;
  url?: string;
  sections: Section[];
  theme: Theme;
  layout: {
    align: "LEFT" | "CENTER" | "RIGHT";
  };
  hideSignature?: boolean;
  /** "Voltar ao topo" (Páginas): a button on the Keepo to go back up once the visitor scrolls; any plan */
  backToTop?: boolean;
  /** The fonts of the titles and of the text, by id (see the catalog of site-page-react-component). Absent:
   * Poppins and Inter. Out of `theme`, so choosing a palette (a whole new theme) keeps them. */
  fonts?: Partial<SiteFonts>;
  /** Search and sharing settings (PLUS). Used by the backend to build the published page's <head>. */
  seo?: SiteSeo;
  /** Extra pages (PLUS), each a list of sections like the home; the order is the menu's. See model/pages. */
  pages?: SitePage[];
  /** The pages menu on the published Keepo. */
  menu?: SiteMenu;
  /** Set only on a draft created through the free-Keepos-limit "Criar Keepo PLUS"
   * flow, where clicking the modal is already the user's consent to subscribe. Lets
   * the first draft save bypass the free-limit check server-side (see
   * SaveDraftForm.php's `intendedSubscription`), since the site isn't actually PLUS
   * until checkout completes. */
  intendedSubscription?: "PLUS";
}

export interface SiteSeo {
  /** Page title in search results and browser tabs (falls back to the Keepo's title). */
  title?: string;
  description?: string;
  /** ISO language code of the page, e.g. "pt-BR" or "en". */
  language?: string;
  /** Asks search engines not to index the page (robots noindex). */
  noIndex?: boolean;
  /** Image shown when the link is shared (1200 x 630). */
  shareImage?: string;
  favicon?: string;
}

export interface Section {
  id: string;
  type: string;
  active: boolean;
  subType?: string;
}

export interface WelcomeSection extends Section {
  featuredTitle?: string;
  supportingText?: string;
  buttonTitle?: string;
  buttonLink?: string;
  logo?: string;
  exibitionName?: string;
}

export interface AboutSection extends Section {
  pageTitle?: string;
  image?: string;
  featuredText?: string;
  completeDescription?: string;
}

export interface ItemSectionCatalog {
  id: string;
  /** false hides the item from the page; absent means visible (items saved before this field). The
   * published page only honors it once the renderer filters on it - see `constants.features.itemVisibility`. */
  active?: boolean;
  title: string;
  image: string;
  description: string;
  linkTitle: string;
  link: string;
}

export interface CatalogSection extends Section {
  pageTitle: string;
  items: ItemSectionCatalog[];
}

export interface ItemSectionLinks {
  id: string;
  /** false hides the item from the page; absent means visible (items saved before this field). The
   * published page only honors it once the renderer filters on it - see `constants.features.itemVisibility`. */
  active?: boolean;
  title: string;
  link: string;
  /** Points the item at a page of this Keepo (model/pages); the published page builds the link from it. */
  pageId?: string;
  thumbnailType: "IMAGE" | "EMOJI" | "NONE";
  image?: string;
  emoji?: string;
}

export interface LinksSection extends Section {
  pageTitle: string;
  items: ItemSectionLinks[];
}

export interface SocialSection extends Section {
  facebook?: string;
  instagram?: string;
  twitter?: string;
  youtube?: string;
  linkedin?: string;
  whatsapp?: string;
  behance?: string;
  dribbble?: string;
  medium?: string;
  twitch?: string;
  tiktok?: string;
  vimeo?: string;
  pinterest?: string;
  /** The shape around the icons on the page: a circle (also when absent) or a square with rounded corners. */
  iconShape?: SocialIconShape;
  /** The shape filled (also when absent) or only its outline. */
  iconFill?: SocialIconFill;
}

export type SocialIconShape = "ROUND" | "SQUARE";
export type SocialIconFill = "FILLED" | "OUTLINE";

export interface FloatingWhatsappSection extends Section {
  whatsapp?: string;
  message?: string;
}

export interface MediaSection extends Section {
  subType: string;
  link?: string;
  pageTitle?: string;
  image?: string;
  imageDisplayMode?: "cover" | "contain";
  imageAspectRatio?: string;
}

export interface FormSection extends Section {
  pageTitle: string;
  fields: {
    name: FormField;
    email: FormField;
    phone: FormField;
    message: FormField;
  };
  /** Fields defined by the user (name, type, order). When set, `fields` is derived from it
   * for the current page renderer, which only knows the four fixed fields. */
  customFields?: FormCustomField[];
  buttonText: string;
  successRedirectLink?: string;
  successMessage?: string;
  storage: Storage;
}

export interface Storage {
  email: {
    active: boolean;
    value?: string;
  };
  mailChimp: MailChimpStorage;
  googleDrive?: GoogleDriveStorage;
  /** Each contact told to this WhatsApp (PLUS, like the form): the number as typed. */
  whatsapp?: {
    active: boolean;
    value?: string;
  };
}

export interface MailChimpStorage {
  active: boolean;
  connectionId?: string;
  audienceList?: {
    id: string;
    name: string;
  };
}

export interface GoogleDriveStorage {
  active: boolean;
  connectionId?: string;
  spreadsheet?: {
    id: string;
    name: string;
  };
}

export interface FormField {
  active: boolean;
}

export const createNewSite = (): Site => {
  return {
    title: "",
    // No field until the user picks one (a preset one would look chosen and skew the stats)
    category: "",
    sections: [],
    theme: preDefinedThemes[0],
    layout: {
      align: "LEFT",
    },
    hideSignature: false,
  };
};

export interface FaqItem {
  id: string;
  /** false hides the item from the page; absent means visible (items saved before this field). The
   * published page only honors it once the renderer filters on it - see `constants.features.itemVisibility`. */
  active?: boolean;
  question: string;
  answer: string;
}

export interface FaqSection extends Section {
  pageTitle: string;
  items: FaqItem[];
}

export interface HighlightsSection extends Section {
  pageTitle: string;
  /** The section's own, also set by the Personalizar's Alinhamento (the last change wins). */
  alignment: "LEFT" | "CENTER" | "RIGHT";
  items: HighlightItem[];
}

export interface HighlightItem {
  id: string;
  /** false hides the item from the page; absent means visible (items saved before this field). The
   * published page only honors it once the renderer filters on it - see `constants.features.itemVisibility`. */
  active?: boolean;
  title: string;
  description?: string;
  thumbnailType: "IMAGE" | "EMOJI";
  image?: string;
  emoji?: string;
}

export interface TestimonialsSection extends Section {
  pageTitle?: string;
  /** One at a time, sliding (also when absent), or all of them one under the other. */
  layout?: "SLIDE" | "LIST";
  alignment: "LEFT" | "CENTER" | "RIGHT";
  items: TestimonialItem[];
}

export interface TestimonialItem {
  id: string;
  /** false hides the testimonial from the page; absent means visible. */
  active?: boolean;
  /** What was said (the page draws the quotes around it). */
  text: string;
  /** Who said it. */
  name: string;
  /** Stars, 1 to 5. */
  rating: number;
  /** The photo of who said it; without it, the page shows the initial of the name. */
  image?: string;
}

/** The limits of a testimonials section, the same the API cuts at (see `KeepoDraft::TESTIMONIALS_*`). */
export const TESTIMONIALS_MAX_ITEMS = 10;
export const TESTIMONIALS_MAX_TITLE = 60;
export const TESTIMONIAL_MAX_TEXT = 300;
export const TESTIMONIAL_MAX_NAME = 60;

/** A currency by its ISO 4217 code ("BRL", "USD", "JPY"); a new section is in reais. */
export type PricingCurrency = string;

export interface PricingSection extends Section {
  pageTitle?: string;
  /** The currency of every price of the section. */
  currency: PricingCurrency;
  items: PricingItem[];
}

export interface PricingItem {
  id: string;
  /** false hides the plan from the page; absent means visible. */
  active?: boolean;
  title: string;
  thumbnailType?: "IMAGE" | "EMOJI" | "NONE";
  image?: string;
  emoji?: string;
  price?: number;
  /** The price before, struck through on the page. */
  oldPrice?: number;
  /** How many installments and the value of each (with interest it isn't the price divided). */
  installments?: number;
  installmentPrice?: number;
  benefits: string[];
  buttonText: string;
  /** Where the button goes: a link, or a WhatsApp number (with a message). */
  buttonType: "LINK" | "WHATSAPP";
  link?: string;
  whatsapp?: string;
  message?: string;
  /** The plan to look at first: a badge on top and an outline on the page. */
  featured?: boolean;
  /** The text of the badge; without it, "most popular" in the language of the Keepo. */
  featuredLabel?: string;
}

/** The limits of a pricing section, the same the API cuts at (see `KeepoDraft::PRICING_*`). */
export const PRICING_MAX_ITEMS = 10;
export const PRICING_MAX_TITLE = 60;
export const PRICING_MAX_PAGE_TITLE = 60;
export const PRICING_MAX_BENEFITS = 10;
export const PRICING_MAX_BENEFIT = 120;
export const PRICING_MAX_BUTTON_TEXT = 40;
export const PRICING_MAX_MESSAGE = 300;
export const PRICING_MAX_INSTALLMENTS = 9999;
export const PRICING_MAX_FEATURED_LABEL = 24;

export interface LocationSection extends Section {
  pageTitle: string;
  address: string;
  latitude?: number;
  longitude?: number;
}

export interface SitePage {
  id: string;
  /** The page's address on the Keepo (`/contato`). */
  slug: string;
  /** Name in the menu and in the editor. */
  title: string;
  sections: Section[];
  showInMenu: boolean;
  seo?: { title?: string; description?: string; shareImage?: string };
  /** Which ready-made page it came from (phase 2). */
  template?: string;
}

export interface SiteMenu {
  enabled: boolean;
}
